import React, { useState } from 'react'
import { useLocalstorageState } from 'rooks'
import { Banner } from '@mobi/component-library/Common/V2/Banner'
import { Button } from '@mobi/component-library/Common/V2/Buttons'
import { useBetAccount } from '@mobi/account/Hooks'
import { useLogon } from '@core/Utils/hooks/useLogon'
import { autoOptInMarketingCommunications } from '@core/Areas/Account/MyPreferences/helpers/autoOptInMarketingCommunications'
import { useNavigate } from 'react-router'
import { useFeature } from '@core/Utils/hooks'
import { FeatureFlags } from '@mobi/settings/featureFlags'
import { incrementQuickCounter } from '@core/Services/Analytics/helpers/quickCounter'
import { useCognitoUser } from '@core/Data/Account/useCognitoUser'
import { fetchCognitoIsAuthenticated } from '@core/Areas/Settings/Components/MFASettings/Hooks/useCognitoUserIsAuthenticated'
import { MFAMethod } from '@mobi/api-types'
import { useQuery } from '@tanstack/react-query'

export const MarketingOptInBanner: React.FC = () => {
  const { accountNumber, isLoggedIn } = useLogon()
  const isUserLoggedIn = accountNumber !== null && isLoggedIn === true

  if (!isUserLoggedIn || !accountNumber) return null

  return (
    <>
      <MarketingOptInBannerComponent accountNumber={accountNumber} />
      <MFAOptInBannerComponent accountNumber={accountNumber} />
    </>
  )
}

// ===============
// Local Component
// ===============

const MarketingOptInBannerComponent: React.FC<{ accountNumber: number }> = ({ accountNumber }) => {
  const [dimissedTimestamp, setDismissedTimestamp] = useLocalstorageState<string | null>(
    `marketing-optin:${(accountNumber + 9).toString(36).toUpperCase()}:dismissed`,
    null
  )

  const { data } = useBetAccount()

  if (dimissedTimestamp) return null

  const marketingOptInInd = data?.MarketingOptInInd
  if (!marketingOptInInd) return null

  const hasOptedIn = marketingOptInInd === 'Y'
  if (hasOptedIn) return null

  const handleDismiss = () => {
    setDismissedTimestamp(new Date().toISOString())
  }

  const handleOptIn = () => {
    handleDismiss()
    autoOptInMarketingCommunications()
  }

  return (
    <Banner
      hideIcon
      type='warning'
      onClickDismiss={handleDismiss}
      data-testid='MarketingOptInBanner'
      text='Opt in now to receive promotions, bonus bets, marketing and offers by email, post, SMS, telephone and other direct means.'
      button={
        <Button
          size='sm'
          role='link'
          onClick={handleOptIn}
          color='secondary_outline'
          style={{ maxWidth: 'fit-content' }}
        >
          Opt In
        </Button>
      }
    />
  )
}

const MFAOptInBannerComponent: React.FC<{ accountNumber: number }> = ({ accountNumber }) => {
  const navigate = useNavigate()
  const isMFAFeatureActive = useFeature(FeatureFlags.MFA_PREFERENCE_SETTINGS.key)
  const [isLoading, setIsLoading] = useState(false)

  const MfaBannerRequirement = useFeature(FeatureFlags.MFA_OPT_IN_BANNER.key) as
    | 'off'
    | 'on_if_recent_login'
    | 'on'

  const isMFABannerEnabled = MfaBannerRequirement !== 'off'

  const [dismissedTimestamp, setDismissedTimestamp] = useLocalstorageState<string | null>(
    `mfa-optin:${(accountNumber + 9).toString(36).toUpperCase()}:dismissed`,
    null
  )

  const isUserEligibleForBanner =
    isMFAFeatureActive && isMFABannerEnabled && dismissedTimestamp === null

  const { data: isUserRecentlyAuthenticated } = useQuery({
    queryFn: async () => {
      try {
        const response = await fetchCognitoIsAuthenticated()
        return response.expiresIn > 30
      } catch {
        return false
      }
    },
    queryKey: ['isUserRecentlyAuthenticated', accountNumber],
    retry: false,
    refetchOnWindowFocus: false,
    enabled: isUserEligibleForBanner,
    staleTime: 300_000,
  })

  const { data: user } = useCognitoUser(accountNumber, {
    enabled:
      MfaBannerRequirement === 'on' ||
      (isUserRecentlyAuthenticated === true && MfaBannerRequirement === 'on_if_recent_login'),
  })

  if (!isUserEligibleForBanner) {
    return null
  }

  if (user?.preferredFactor !== 'None') {
    return null
  }

  if (MfaBannerRequirement == 'on_if_recent_login' && !isUserRecentlyAuthenticated) {
    return null
  }

  const handleDismiss = () => {
    setDismissedTimestamp(new Date().toISOString())
    incrementQuickCounter('mfa-banner-click-dismiss')
  }

  const handleOptInClick = async () => {
    setIsLoading(true)

    setDismissedTimestamp(new Date().toISOString())
    incrementQuickCounter('mfa-banner-click-optin')

    if (isUserRecentlyAuthenticated) {
      incrementQuickCounter('mfa-banner-click-optin-authd')
    }

    setTimeout(() => {
      setIsLoading(false)

      navigate('/account/settings/mfa/', {
        state: {
          startVerifyOnLoad: isUserRecentlyAuthenticated ? 'SMS' : undefined,
        } as MfaOptInBannerNavigationState,
      })
    }, 80)
  }

  return (
    <Banner
      hideIcon
      type='warning'
      onClickDismiss={handleDismiss}
      data-testid='MFAOptInBanner'
      text='Enable Multi-factor Authentication on your account for enhanced security'
      button={
        <Button
          size='sm'
          role='link'
          onClick={handleOptInClick}
          color='secondary_outline'
          isLoading={isLoading}
          style={{ maxWidth: 'fit-content' }}
        >
          MFA Settings
        </Button>
      }
    />
  )
}

export type MfaOptInBannerNavigationState = { startVerifyOnLoad?: MFAMethod }
